import FooterLink from "@/components/my-ui/links/FooterLink";
import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { getGtmUserData } from "@/helpers/getGtmUserData";
import { sendGTMEvent } from "@/helpers/sendGTMEvent";
import { useCartActions } from "@/hooks/useCartActions";
import { useRouter } from "@/i18n/navigation";
import { useAuth } from "@/providers/AuthProvider";
import { usePromoCode } from "@/providers/PromoCodeProvider";
import { ArrowUpRight, ShoppingCart } from "lucide-react";
import { useTranslations } from "next-intl";
import ApplyCouponCode from "./ApplyCouponCode";
import CartItem from "./CartItem";
import { useCheckoutSummary } from "@/hooks/useCheckoutSummary";
import { Skeleton } from "@/components/ui/skeleton";

export interface PropsType {
  setShowCart: React.Dispatch<React.SetStateAction<boolean>>;
}

function CartDetail({ setShowCart }: PropsType) {
  const t = useTranslations("Shared.cartSheet");
  const { user } = useAuth();
  const router = useRouter();
  const { cartItems, totalCartPrice } = useCartActions();
  const { promoCodeData } = usePromoCode();
  const { checkoutSummary, isSummaryLoading } = useCheckoutSummary();

  function handleCheckoutClick() {
    if (cartItems.length !== 0) {
      const items = cartItems.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        affiliation: "eSIMCard Web",
        item_brand: "eSIMCard",
        item_category: item.package_type,
        coupon: promoCodeData ? promoCodeData.promo_code : "",
      }));

      const checkoutValue = promoCodeData
        ? promoCodeData.total_amount
        : totalCartPrice;

      sendGTMEvent({
        event: "begin_checkout",
        user_data: getGtmUserData(user?.name),
        ecommerce: {
          currency: "USD",
          value: checkoutValue,
          items: items,
        },
      });
    }

    router.push("/checkout");
  }

  return cartItems.length !== 0 ? (
    <>
      <SheetHeader className="!h-max border-b px-6 pb-4 pt-6">
        <SheetTitle className="flex items-center gap-2.5 font-montserrat">
          <ShoppingCart size={24} />
          {t(`title`)}
        </SheetTitle>
      </SheetHeader>

      {/* Cart Items */}
      <div className="flex flex-grow flex-col justify-start gap-4 overflow-auto py-4 scrollbar-thin">
        {cartItems.map((item, index) => (
          <CartItem cartItem={item} key={index} />
        ))}
      </div>

      {/* Coupon and Checkout */}
      <div className="border-t px-4 pb-3 pt-3 sm:px-6">
        <ApplyCouponCode />
        <div className="mb-2 flex items-center justify-between">
          <p className="font-montserrat text-lg font-semibold">
            {t(`subTotal`)}
          </p>
          <span className="flex items-center justify-center rounded-[6px] bg-primary/15 px-[12px] py-[4px] text-base font-medium text-primary">
            ${totalCartPrice}
          </span>
        </div>
        {/* Checkout Button */}
        <div className="flex flex-col gap-1">
          <Button
            className="group flex w-full items-center justify-center gap-2 text-base"
            size={"md"}
            onClick={handleCheckoutClick}
          >
            {t(`checkoutBtn`)}

            {isSummaryLoading ? (
              <Skeleton className="h-5 w-10 animate-pulse rounded-md bg-white/20" />
            ) : (
              <span>
                $
                {checkoutSummary?.total_amount
                  ? checkoutSummary.total_amount.toFixed(2)
                  : totalCartPrice}
              </span>
            )}

            <ArrowUpRight
              className="transition-transform group-hover:rotate-[45deg]"
              size={18}
            />
          </Button>
          <Button
            variant={"ghost"}
            size={"sm"}
            className="text-base hover:text-primary"
            onClick={() => {
              setShowCart(false);
            }}
          >
            {t(`continueBtnText`)}
          </Button>
        </div>
      </div>
    </>
  ) : (
    <div className="ms-6 mt-4 flex flex-col gap-2">
      <h2 className="text-lg font-semibold">{t("emptyCart.title")}</h2>
      <p className="mt-2 text-sm">{t("emptyCart.description")}</p>

      <FooterLink
        href={"/esim"}
        className="text-primary underline underline-offset-4"
        onClick={() => setShowCart(false)}
      >
        {t("emptyCart.seePackagesLinkText")}
      </FooterLink>
    </div>
  );
}

export default CartDetail;
